import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import * as Yup from 'yup'
import pluralize from 'pluralize'
import { isEmpty, isEqual, isNil, size } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { AdminApprovalBanner } from '@/views/Organizations/components/AdminApprovalBanner'
import { ConnectionContentTable } from '@/views/Organizations/components/ConnectionContentTable'
import {
  REPLACE_DONE,
  REPLACE_PATCH_FAILED,
  REPLACE_REMOVE_FAILED,
} from '@/views/Organizations/components/ConnectionContentTable.helpers'
import { ConnectionStatusRow } from '@/views/Organizations/components/ConnectionStatusRow'
import {
  HeldLibraryBanners,
  libraryLabelOf,
} from '@/views/Organizations/components/HeldLibraryBanners'
import { ManageFilesModal } from '@/views/Organizations/components/ManageFilesModal'
import { offersManageFiles } from '@/views/Organizations/components/ManageFilesModal.helpers'
import { NotebookSuggestionBanner } from '@/views/Organizations/components/NotebookSuggestionBanner'
import {
  createSharePointConnection,
  updateSharePointConnection,
  removeSharePointAllowlistEntry,
  setSharePointLists,
  setSharePointSizeApproval,
} from '@/reducers/organizations/sharepointConnection.actions'
import {
  GUID_RE,
  HOST_RE,
  PERSONAL_HOST_RE,
  ALLOWLIST_SCHEMA,
  ALLOWLIST_ROW_SCHEMA,
  BLANK_ALLOWLIST_ROW,
  LIBRARY_TYPE,
  LIST_TYPE,
  WHOLE_SITE_TYPE,
  SELECTOR_TYPE_OPTIONS,
  namesOneThing,
  toFormAllowlist,
  toServerAllowlist,
  toServerEntry,
  normalizedLibrary,
  isSiteWideSelector,
  sitePathOf,
  sameSelector,
  entrySelectsAList,
  affectedSyncRows,
  sharePointManageFilesRoots,
  sharePointSyncRowsFor,
  SYNC_ERROR_LABELS,
  awaitingAdminApproval,
  syncRowLibraryLabel,
  connectionHasSiteRows,
  connectionListsEnabled,
  sharedIncludeLists,
  withServerIncludeLists,
} from '@/views/Organizations/components/SharePointConnectionForm.helpers'
import {
  notebookKey,
  notebooksOnSite,
  pendingNotebooks,
  siteAssetsRowFor,
  tableMatchesServer,
  undismissedNotebooks,
  wholeSiteEntryFor,
} from '@/views/Organizations/components/unindexedNotebooks.helpers'

// One phrasing for a form row in confirmation copy, so "what exactly is
// affected" reads the same in every dialog.
const rowLabelOf = (row) => {
  switch (row.selector_type) {
    case LIST_TYPE:
      return `list ${normalizedLibrary(row.name)} in ${sitePathOf(row)}`
    case LIBRARY_TYPE:
      return `library ${normalizedLibrary(row.name)} in ${sitePathOf(row)}`
    default:
      return `${sitePathOf(row)} (whole site)`
  }
}

// Names the row a per-row control belongs to. Every row renders the same
// controls, so without this a screen reader lists N identical "Playbooks this
// row answers". Falls back on a blank row, where there is no identity yet and
// only one such row can be open at a time.
const rowControlLabel = (label, row) =>
  isEmpty(sitePathOf(row)) ? `${label} for this row` : `${label} for ${rowLabelOf(row)}`

// What a removal/replacement erases, shared by both destructive dialogs. The
// enumeration distinguishes "the list is unavailable" (sync state
// loading/failed — the purge still runs on real server data) from "genuinely
// nothing recorded" and from "kept because another entry still selects it",
// so the dialog never understates or overstates the blast radius.
const AffectedDocumentsSummary = ({ affected, syncStateUnavailable, testId }) => (
  <>
    {syncStateUnavailable && (
      <p>
        The ingested-document list is unavailable right now, so what follows can’t be shown — the
        removal still deletes everything this selection has ingested.
      </p>
    )}
    {!syncStateUnavailable && !isEmpty(affected.rows) && (
      <ul data-testid={testId}>
        {affected.rows.map((row) => (
          <li key={`${row.kind}:${row.drive_id ?? row.library_name ?? ''}`}>
            {syncRowLibraryLabel(row)}
            {isNil(row.document_count) ? '' : ` — ${row.document_count} documents`}
          </li>
        ))}
      </ul>
    )}
    {!syncStateUnavailable && isEmpty(affected.rows) && affected.keptByOverlap > 0 && (
      <p>
        Nothing will be deleted: this selection’s content is still selected by another allowlist
        entry, so its documents are kept.
      </p>
    )}
    {!syncStateUnavailable && isEmpty(affected.rows) && affected.keptByOverlap === 0 && (
      <p>No ingested documents are recorded for this selection yet.</p>
    )}
  </>
)

// Destructive confirmation for a persisted-selector removal.
const RemoveEntryConfirmModal = ({
  removeTarget,
  tenantHostname,
  affected,
  syncStateUnavailable,
  removing,
  onConfirm,
  onClose,
}) => (
  <ConfirmModal
    show
    onClose={onClose}
    title="Remove from allowlist and purge documents?"
    confirmLabel="Remove and purge"
    busyLabel="Purging…"
    busy={removing}
    destructive
    onConfirm={onConfirm}
    testIdPrefix="sharepoint-remove-entry"
  >
    {removeTarget.isList && (
      <p>
        This removes list <strong>{removeTarget.name}</strong> in{' '}
        <strong>{removeTarget.sitePath}</strong> from the allowlist and immediately deletes the
        documents built from its items.
      </p>
    )}
    {!removeTarget.isList && removeTarget.siteWide && (
      <p>
        This removes the whole site <strong>{removeTarget.sitePath}</strong> from the allowlist and
        immediately deletes its ingested documents — every library, its Site Pages, and any of its
        lists no other allowlist row still selects.
      </p>
    )}
    {!removeTarget.isList && !removeTarget.siteWide && (
      <p>
        This removes library <strong>{removeTarget.name}</strong> in{' '}
        <strong>{removeTarget.sitePath}</strong> from the allowlist and immediately deletes its
        ingested documents.
      </p>
    )}
    {/* An org can hold several tenants (QCER-1026) and site paths repeat across
        them, so the site alone does not identify what is about to be erased. */}
    <p className="field-help small-text">
      On connection <strong>{tenantHostname}</strong>.
    </p>
    <AffectedDocumentsSummary
      affected={affected}
      syncStateUnavailable={syncStateUnavailable}
      testId="sharepoint-remove-entry-affected"
    />
    <p>Re-adding it later re-ingests from scratch on the next sync. This cannot be undone.</p>
  </ConfirmModal>
)

// Destructive confirmation for editing a persisted selector: a replace is the
// remove-and-purge operation with the new spelling saved first, and the copy
// must own that — documents only the OLD selection covered are deleted now.
const ReplaceEntryConfirmModal = ({
  oldRow,
  newRow,
  tenantHostname,
  affected,
  syncStateUnavailable,
  replacing,
  onConfirm,
  onClose,
}) => (
  <ConfirmModal
    show
    onClose={onClose}
    title="Replace this entry and purge its documents?"
    confirmLabel="Replace and purge"
    busyLabel="Replacing…"
    busy={replacing}
    destructive
    onConfirm={onConfirm}
    testIdPrefix="sharepoint-replace-entry"
  >
    <p>
      This changes <strong>{rowLabelOf(oldRow)}</strong> to <strong>{rowLabelOf(newRow)}</strong>.
      Documents the new selection no longer covers are deleted immediately; what it newly covers
      ingests on the next sync.
    </p>
    <p className="field-help small-text">
      On connection <strong>{tenantHostname}</strong>.
    </p>
    <AffectedDocumentsSummary
      affected={affected}
      syncStateUnavailable={syncStateUnavailable}
      testId="sharepoint-replace-entry-affected"
    />
    <p>This cannot be undone.</p>
  </ConfirmModal>
)

const CONTENT_TABLE_TEST_ID = 'sharepoint-content-table'

const FINISH_EDITING_FIRST = { disabled: true, disabledTooltip: 'Finish editing the entry first' }
const ADDING_SITE_ASSETS = { disabled: true, disabledTooltip: 'Adding the Site Assets row' }

const saveButtonPropsFor = ({ editingEntry, togglingLists, indexingNotebook }) => {
  if (editingEntry) {
    return FINISH_EDITING_FIRST
  }
  if (togglingLists) {
    return { disabled: true, disabledTooltip: 'Saving the lists change' }
  }
  if (indexingNotebook) {
    return ADDING_SITE_ASSETS
  }
  return {}
}

const toggleListsPropsFor = ({ editingEntry, saving, indexingNotebook, onlyNamedLists }) => {
  if (editingEntry) {
    return FINISH_EDITING_FIRST
  }
  if (saving) {
    return { disabled: true, disabledTooltip: 'Saving the connection' }
  }
  if (indexingNotebook) {
    return ADDING_SITE_ASSETS
  }
  if (onlyNamedLists) {
    return {
      disabled: true,
      disabledTooltip: "Only named lists here; remove a list's row to stop syncing it",
    }
  }
  return {}
}

// Index waits until the table is exactly the persisted allowlist its PATCH is built from. An
// open row is named first: one switched to a blank-named library still matches the server.
const indexDisabledReasonFor = ({ editingEntry, busy, matchesServer }) => {
  if (editingEntry) {
    return FINISH_EDITING_FIRST.disabledTooltip
  }
  if (busy) {
    return 'Saving changes'
  }
  if (!matchesServer) {
    return 'Save your table changes first'
  }
  return null
}

// Rendered inside the table so it re-renders with the rows: the form itself only
// sees Formik's values through a ref, which would leave the reason a render stale.
const NotebookSuggestion = ({ persistedAllowlist, editingEntry, busy, ...bannerProps }) => {
  const { values } = useFormikContext()
  return (
    <NotebookSuggestionBanner
      {...bannerProps}
      disabledReason={indexDisabledReasonFor({
        editingEntry,
        busy,
        matchesServer: tableMatchesServer(values.allowlist, persistedAllowlist),
      })}
    />
  )
}

/**
 * One SharePoint connection's detail pane: its status, then the merged
 * content table (allowlist entries carrying their own ingest state), then the
 * connection's details. Create mode when `connection` is null.
 *
 * The list container owns loading and selection, and REMOUNTS this component
 * per selection (AbstractForm has no `enableReinitialize`, so a shared instance
 * would keep the previously selected connection's field values).
 *
 * @param {number} props.organizationId the org being configured
 * @param {Object} props.connection the selected connection, or null to create
 * @param {Object} props.syncState undefined | null | the connection's ingest state
 * @param {Function} props.onSaved awaited after a save/activate; reloads the list
 * @param {Function} props.onClose the form's secondary button action
 * @param {string} props.closeLabel label for that secondary button
 * @param {React.ReactNode} props.footerActions extra footer-left actions
 * @param {Function} props.onDirty reports the form's unsaved-input state upward
 * @param {Function} props.onFormikReady receives the live Formik state each render,
 *   so the container can preserve a draft's values across an unmount
 * @param {Object} props.draftValues create mode only: previously typed values to
 *   restore, when the operator returns to a preserved draft
 * @param {Array} props.playbooks {value: cid, label: name} options for the
 *   per-row playbook scope (QCER-1075); empty until the org's configs load
 * @param {boolean} props.playbooksLoading whether those options are still loading
 * @param {boolean} props.playbooksFailed whether fetching them failed outright
 * @param {Array} props.dismissedNotebooks keys of this connection's notebooks dismissed in
 *   this browser, which the banner skips
 * @param {Function} props.onDismissedNotebooksChange keys => void, called when Not now or a
 *   row link changes those keys
 * @param {boolean} props.supportsManageFiles whether synced rows offer Manage files (set from
 *   the provider registry)
 */
export const SharePointConnectionForm = ({
  organizationId,
  connection,
  syncState,
  onSaved,
  onClose,
  closeLabel = 'Close',
  footerActions = null,
  onDirty,
  onFormikReady,
  draftValues,
  playbooks = [],
  playbooksLoading = false,
  playbooksFailed = false,
  dismissedNotebooks = [],
  onDismissedNotebooksChange = () => {},
  supportsManageFiles = false,
}) => {
  const dispatch = useDispatch()
  const isEdit = !isNil(connection)
  const persistedAllowlist = isEdit ? connection.allowlist : []
  // The row whose files Manage files shows: { subtitle, roots }, or null while it is closed.
  const [manageFilesTarget, setManageFilesTarget] = useState(null)
  // A persisted row's inline edit lives outside Formik, so the form tracks it
  // separately: it locks Save (a half-finished replacement must not ride along
  // on a config save) and arms the modal's unsaved-changes guard.
  const [editingEntry, setEditingEntry] = useState(false)
  // Older agent-gpt sends no lists_default; false is what a missing key meant there.
  const listsDefault = isEdit && connection.lists_default === true
  const listsEnabled = isEdit ? connectionListsEnabled(connection.allowlist, listsDefault) : true
  const newRowLists = isEdit ? sharedIncludeLists(connection.allowlist) : undefined
  const [togglingLists, setTogglingLists] = useState(false)
  const [confirmingListsOff, setConfirmingListsOff] = useState(false)
  // Only Balto staff may let a library past the size limit (RTG3-1300).
  const isBaltoAdmin = useSelector((state) => state.currentUser.organizationid) === 1
  // The held library whose approval is being confirmed; null while none is.
  const [approvalTarget, setApprovalTarget] = useState(null)
  const [approving, setApproving] = useState(false)
  const [saving, setSaving] = useState(false)
  // The live Formik state, so a lists toggle can mirror the server into the rows.
  const formikRef = useRef(null)
  // The form remounts per connection, so a request can resolve after this instance is gone.
  const mounted = useRef(true)
  const notebooks = isEdit ? pendingNotebooks(connection) : []
  // The site whose row link was clicked, so the banner shows that site's notebook.
  const [requestedSitePath, setRequestedSitePath] = useState(null)
  const [indexingNotebook, setIndexingNotebook] = useState(false)
  // The table edits Formik before it awaits its reload, so until that lands the
  // persisted allowlist is stale and an insert built from it could undo the change.
  const [reloadingTable, setReloadingTable] = useState(false)
  const [bannerFocusRequested, setBannerFocusRequested] = useState(false)
  // Banner and Button do not forward refs, so focus lands on a wrapper.
  const bannerRef = useRef(null)
  const suggestedNotebooks = isEdit ? undismissedNotebooks(connection, dismissedNotebooks) : []
  const requestedNotebooks = isNil(requestedSitePath)
    ? []
    : notebooksOnSite(suggestedNotebooks, requestedSitePath)
  const suggestion = isEmpty(requestedNotebooks) ? suggestedNotebooks[0] : requestedNotebooks[0]
  const saveButtonProps = saveButtonPropsFor({ editingEntry, togglingLists, indexingNotebook })
  const toggleListsProps = toggleListsPropsFor({
    editingEntry,
    saving,
    indexingNotebook,
    onlyNamedLists: isEdit && !connectionHasSiteRows(connection.allowlist),
  })

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Formik never reinitializes, so a reload landing on a clean form (one remounted while its
  // own request was in flight) adopts the server's rows. Unsaved edits are never replaced.
  useEffect(() => {
    if (!isEdit || editingEntry) {
      return
    }
    const { values, dirty, resetForm } = formikRef.current
    const serverRows = toFormAllowlist(persistedAllowlist)
    // A clean form holds a blank row only for a connection with no entries, and the table
    // keeps that row open as a draft, so a server row must not take its place.
    const blankRowOpen = isEqual(values.allowlist, [BLANK_ALLOWLIST_ROW])
    if (dirty || blankRowOpen || isEqual(values.allowlist, serverRows)) {
      return
    }
    resetForm({ values: { ...values, allowlist: serverRows } })
  }, [persistedAllowlist, editingEntry])

  useEffect(() => {
    if (!bannerFocusRequested) {
      return
    }
    bannerRef.current?.focus()
    setBannerFocusRequested(false)
  }, [bannerFocusRequested])

  const saveConnection = async (values, { resetForm, setValues }) => {
    // Validation guarantees every row has a Site (and a "One …" row a name)
    // and that there's at least one row, so the rows only need mapping onto
    // the server's entry shape — one name key per row, and include_lists only
    // where it applies (QCER-1089). Saving activates the connection; the
    // Pause syncing button pauses it independently.
    const allowlist = toServerAllowlist(values.allowlist, { newRowLists })
    // The optional Label is a text input, so "unnamed" arrives as ''. Send NULL
    // instead: agent-gpt PATCHes with exclude_unset, so an explicit null clears
    // a name the operator deleted, while omitting it on create lets the column
    // default to NULL rather than storing '' as the connection's name.
    const label = isEmpty(values.label.trim()) ? null : values.label.trim()
    if (isEdit) {
      const updated = await dispatch(
        updateSharePointConnection({
          connectionId: connection.id,
          patch: {
            label,
            tenant_hostname: values.tenant_hostname,
            allowlist,
            // Saving takes a connection live (master behaviour since QCER-1002)
            // — EXCEPT on one whose documents were erased. agent-gpt clears
            // `purged_at` whenever it receives active=true, so letting a config
            // save activate an offboarded connection would delete the erasure
            // record and re-ingest the tenant that was just erased, with no
            // confirmation. Going live again is the Resume syncing button's job.
            ...(isNil(connection.purged_at) ? { active: true } : {}),
          },
          successMessage: 'SharePoint connection saved',
        })
      )
      // The form is not remounted after a save, so a saved edit has to move the
      // baseline itself or the close guard keeps warning about it. Input typed
      // while the PATCH was in flight was not saved, so it stays dirty.
      if (!isNil(updated) && mounted.current) {
        const liveValues = formikRef.current.values
        resetForm({ values })
        if (!isEqual(liveValues, values)) {
          setValues(liveValues)
        }
      }
      await onSaved()
      return
    }
    const created = await dispatch(
      createSharePointConnection({
        organizationId,
        label: label ?? undefined,
        tenantId: values.tenant_id,
        tenantHostname: values.tenant_hostname,
        allowlist,
        active: true,
      })
    )
    // Select the connection just created rather than whatever sorts first, so
    // the operator lands on their own new tenant. A failed create (null) leaves
    // the selection alone; the thunk has already toasted it.
    await onSaved(created?.id)
  }

  // Held through the save: a lists toggle landing mid-save could be undone by this PATCH.
  const handleSave = async (values, formikHelpers) => {
    setSaving(true)
    try {
      await saveConnection(values, formikHelpers)
    } finally {
      if (mounted.current) {
        setSaving(false)
      }
    }
  }

  const handleToggleActive = async () => {
    const goingLive = !connection.active
    if (
      goingLive &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Turn on syncing for ${connection.tenant_hostname}? Your Microsoft 365 admin must ` +
          'already have granted Balto read access to every site below.'
      )
    ) {
      return
    }
    await dispatch(
      updateSharePointConnection({
        connectionId: connection.id,
        patch: { active: goingLive },
        successMessage: goingLive ? 'Connection activated' : 'Connection deactivated',
      })
    )
    await onSaved()
  }

  const toggleLists = async () => {
    // Held through the reload: until `connection` refreshes, a Save would
    // serialize the pre-toggle lists setting.
    setTogglingLists(true)
    try {
      const updated = await dispatch(
        setSharePointLists({ connectionId: connection.id, enabled: !listsEnabled })
      )
      if (!isNil(updated) && mounted.current) {
        const { values, initialValues, dirty, resetForm, setValues } = formikRef.current
        const mirror = (formValues) => ({
          ...formValues,
          allowlist: withServerIncludeLists(formValues.allowlist, updated.allowlist),
        })
        // The toggle is already saved, so it moves the baseline; unsaved edits stay dirty.
        resetForm({ values: mirror(initialValues) })
        if (dirty) {
          setValues(mirror(values))
        }
      }
      await onSaved()
      return updated
    } finally {
      if (mounted.current) {
        setTogglingLists(false)
      }
    }
  }

  const reloadAfterTableChange = async () => {
    setReloadingTable(true)
    try {
      await onSaved()
    } finally {
      if (mounted.current) {
        setReloadingTable(false)
      }
    }
  }

  // A Site Assets row with the Whole site row's playbooks. Only the allowlist is sent:
  // unlike Save, this must never take a paused or erased connection live.
  const indexNotebook = async (notebook) => {
    const row = siteAssetsRowFor(wholeSiteEntryFor(connection.allowlist, notebook.site_path))
    setIndexingNotebook(true)
    try {
      const updated = await dispatch(
        updateSharePointConnection({
          connectionId: connection.id,
          patch: { allowlist: [...connection.allowlist, toServerEntry(row, { newRowLists })] },
          successMessage: `Site Assets added on ${notebook.site_path}. It is indexed on the next sync.`,
        })
      )
      // A failed PATCH changed nothing and the thunk toasted it, so the banner stays.
      if (isNil(updated)) {
        return
      }
      // Saved, so the row moves the baseline; unsaved edits stay dirty. Index only
      // runs while the rows match the server, so appending lands after the persisted rows.
      if (mounted.current) {
        const { values, initialValues, dirty, resetForm, setValues } = formikRef.current
        resetForm({ values: { ...initialValues, allowlist: [...initialValues.allowlist, row] } })
        if (dirty) {
          setValues({ ...values, allowlist: [...values.allowlist, row] })
        }
      }
      await onSaved()
    } finally {
      if (mounted.current) {
        setIndexingNotebook(false)
      }
    }
  }

  const dismissNotebook = (notebook) => {
    onDismissedNotebooksChange([...dismissedNotebooks, notebookKey(notebook)])
  }

  // Brings the site's banner back and moves focus to it, whatever else is suggested.
  const restoreNotebooks = (sitePath) => {
    const siteKeys = notebooksOnSite(notebooks, sitePath).map(notebookKey)
    onDismissedNotebooksChange(dismissedNotebooks.filter((key) => !siteKeys.includes(key)))
    setRequestedSitePath(sitePath)
    setBannerFocusRequested(true)
  }

  const notebookLinkFor = (row, { persisted }) => {
    if (!persisted || row.selector_type !== WHOLE_SITE_TYPE) {
      return null
    }
    // Dismissed or not: this link is how a dismissed notebook comes back.
    const siteNotebooks = notebooksOnSite(notebooks, sitePathOf(row))
    if (isEmpty(siteNotebooks)) {
      return null
    }
    return (
      <Button link xs disabled={indexingNotebook} onClick={() => restoreNotebooks(sitePathOf(row))}>
        {`${pluralize('notebook', size(siteNotebooks), true)} not indexed`}
      </Button>
    )
  }

  const approveSize = async () => {
    setApproving(true)
    const updated = await dispatch(
      setSharePointSizeApproval({
        connectionId: connection.id,
        driveId: approvalTarget.drive_id,
        approved: true,
      })
    )
    if (!isNil(updated)) {
      await onSaved(connection.id)
    }
    if (mounted.current) {
      setApproving(false)
      if (!isNil(updated)) {
        setApprovalTarget(null)
      }
    }
  }

  // Turning lists off purges, so it confirms first; turning them on does not.
  const handleToggleLists = async () => {
    if (listsEnabled) {
      setConfirmingListsOff(true)
      return
    }
    await toggleLists()
  }

  // A failed purge keeps the dialog open, like the removal confirmations.
  const handleConfirmListsOff = async () => {
    const updated = await toggleLists()
    if (!isNil(updated)) {
      setConfirmingListsOff(false)
    }
  }

  // The removal thunk's arguments for one server-shaped entry. Exactly one of
  // the two name params, never both: the endpoint refuses a request that sets
  // library_name and list_name together, and a list removal rides its own
  // path so a rolled-back agent-gpt 404s instead of purging the whole site
  // (QCER-1089).
  const removalArgsOf = (entry) => ({
    connectionId: connection.id,
    sitePath: sitePathOf(entry),
    libraryName:
      entrySelectsAList(entry) || isSiteWideSelector(entry.library_name)
        ? undefined
        : normalizedLibrary(entry.library_name),
    listName: entrySelectsAList(entry) ? normalizedLibrary(entry.list_name) : undefined,
  })

  // The replace runs additive-first: the new spelling is saved alongside the
  // old entry, THEN the old one is removed through the dedicated
  // remove-and-purge operation — whose purge spares whatever the remaining
  // entries (the new one included) still cover. Every failure mode leaves the
  // server self-consistent: a failed PATCH changed nothing, and a failed
  // removal leaves old + new both configured with nothing purged.
  const replaceEntry = async ({ oldEntry, newEntry }) => {
    const patched = await dispatch(
      updateSharePointConnection({
        connectionId: connection.id,
        patch: { allowlist: [...connection.allowlist, toServerEntry(newEntry, { newRowLists })] },
        successMessage: 'New entry saved',
      })
    )
    if (isNil(patched)) {
      return REPLACE_PATCH_FAILED
    }
    const removed = await dispatch(
      removeSharePointAllowlistEntry(removalArgsOf(toServerEntry(oldEntry)))
    )
    return isNil(removed) ? REPLACE_REMOVE_FAILED : REPLACE_DONE
  }

  const targetOf = (row) => {
    const isList = row.selector_type === LIST_TYPE
    return {
      sitePath: sitePathOf(row),
      isList,
      name: normalizedLibrary(row.name),
      siteWide: !isList && !namesOneThing(row),
      // The server-shaped entry the removal targets, so the identity helpers
      // here and on the server compare the same thing.
      entry: toServerEntry(row),
    }
  }

  // Lists are outside Manage files, and a row with no synced library has nothing to show yet.
  const manageFilesActions = (row, covered, index) => {
    const roots = sharePointManageFilesRoots(covered)
    if (row.selector_type === LIST_TYPE || !offersManageFiles(roots)) {
      return []
    }
    return [
      {
        label: 'Manage files',
        onClick: () => setManageFilesTarget({ subtitle: rowLabelOf(row), roots }),
        testId: `${CONTENT_TABLE_TEST_ID}-manage-files-${index}`,
      },
    ]
  }

  const schema = [
    {
      raw: true,
      name: 'allowlist',
      defaultValue: [{ ...BLANK_ALLOWLIST_ROW }],
      customValidators: [ALLOWLIST_SCHEMA],
      as: (
        <ConnectionContentTable
          key="allowlist"
          connectionId={isEdit ? connection.id : null}
          persistedAllowlist={persistedAllowlist}
          syncState={syncState}
          title="Site content"
          caption={
            'One row per thing to index. Site is the site path, e.g. /sites/Marketing. ' +
            'Type decides what gets indexed, and Name applies to the two "One …" types ' +
            'only — a list is indexed as one document per item. Playbooks limits which ' +
            'playbooks can be answered from the row; leave it empty and the row answers ' +
            'every playbook. Changing it re-tags the documents already ingested.'
          }
          notice={
            <>
              {isEdit && connection.active && awaitingAdminApproval(syncState) && (
                <div className="mbs">
                  <AdminApprovalBanner tenantId={connection.tenant_id} />
                </div>
              )}
              <HeldLibraryBanners
                // The panel keeps the last selection's sync state while a create form is open.
                syncState={isEdit ? syncState : undefined}
                canApprove={isBaltoAdmin}
                onApprove={setApprovalTarget}
              />
              {!isNil(suggestion) && (
                <div ref={bannerRef} tabIndex={-1} className="mbs">
                  <NotebookSuggestion
                    notebook={suggestion}
                    persistedAllowlist={persistedAllowlist}
                    editingEntry={editingEntry}
                    busy={saving || togglingLists || reloadingTable || indexingNotebook}
                    indexing={indexingNotebook}
                    onIndex={() => indexNotebook(suggestion)}
                    onDismiss={() => dismissNotebook(suggestion)}
                  />
                </div>
              )}
            </>
          }
          locked={indexingNotebook}
          columns={[
            {
              name: 'site_path',
              header: 'Site',
              placeholder: '/sites/SiteName',
              showError: true,
              note: notebookLinkFor,
            },
            {
              name: 'selector_type',
              header: 'Type',
              control: 'select',
              options: SELECTOR_TYPE_OPTIONS,
              ariaLabel: 'Row type',
              // Changing Type clears a name the new shape has no use for, so a
              // stale value can never be submitted (or linger, greyed out, in
              // the input while the row means something else).
              onSelect: (row, value) => ({
                ...row,
                selector_type: value,
                name: value === LIBRARY_TYPE || value === LIST_TYPE ? row.name : '',
              }),
            },
            {
              name: 'name',
              header: 'Name',
              placeholderOf: (row) => {
                if (row.selector_type === LIST_TYPE) {
                  return 'Events'
                }
                if (row.selector_type === LIBRARY_TYPE) {
                  return 'Documents'
                }
                return 'Not needed for this type'
              },
              disabledWhen: (row) => !namesOneThing(row),
              showError: true,
              blankLabel: '—',
            },
            {
              name: 'config_cids',
              header: 'Playbooks',
              control: 'multiselect',
              options: playbooks,
              loading: playbooksLoading,
              // Locked whenever the options are absent — failed OR still
              // loading. They are what renders the saved scope, so without
              // them the control shows nothing selected while the row still
              // holds its cids: an operator editing that would believe they
              // are setting scope from scratch and instead append to a scope
              // they cannot see.
              disabled: playbooksFailed || playbooksLoading,
              placeholder: playbooksFailed ? 'Playbooks unavailable' : 'All playbooks',
              blankLabel: 'All playbooks',
              ariaLabelOf: (row) => rowControlLabel('Playbooks answered by', row),
              // Confirmed in place rather than through the replace flow: it
              // changes who may retrieve the documents, never which documents
              // exist, so it cannot leave config and ingested data disagreeing
              // the way an edited Site or Name would.
              carried: true,
            },
          ]}
          blankRow={BLANK_ALLOWLIST_ROW}
          addLabel="Add site"
          entrySchema={ALLOWLIST_ROW_SCHEMA}
          sameEntry={(rowA, rowB) => sameSelector(toServerEntry(rowA), toServerEntry(rowB))}
          normalizeEntry={(row) => ({
            site_path: sitePathOf(row),
            selector_type: row.selector_type,
            name: normalizedLibrary(row.name),
          })}
          syncRowsFor={(row, persistedEntries, syncRows) =>
            sharePointSyncRowsFor(toServerEntry(row), persistedEntries, syncRows, { listsDefault })
          }
          errorLabels={SYNC_ERROR_LABELS}
          rowLabel={syncRowLibraryLabel}
          onEditingChange={setEditingEntry}
          onRemoved={reloadAfterTableChange}
          draftRemoveLabel="Remove site"
          persistedRemoveLabel={() => 'Remove and purge documents'}
          removeTargetOf={targetOf}
          removeEntry={(target) =>
            dispatch(removeSharePointAllowlistEntry(removalArgsOf(target.entry)))
          }
          survivesTarget={(row, target) => sameSelector(toServerEntry(row), target.entry)}
          renderRemoveConfirmModal={({ target, removing, onConfirm, onClose: onModalClose }) => (
            <RemoveEntryConfirmModal
              removeTarget={target}
              tenantHostname={connection.tenant_hostname}
              affected={affectedSyncRows(syncState, persistedAllowlist, target.entry, {
                listsDefault,
              })}
              syncStateUnavailable={isNil(syncState)}
              removing={removing}
              onConfirm={onConfirm}
              onClose={onModalClose}
            />
          )}
          replaceEntry={replaceEntry}
          renderReplaceConfirmModal={({
            oldEntry,
            newEntry,
            replacing,
            onConfirm,
            onClose: onModalClose,
          }) => (
            <ReplaceEntryConfirmModal
              oldRow={oldEntry}
              newRow={newEntry}
              tenantHostname={connection.tenant_hostname}
              // The impact of removing the OLD selector once the NEW one is
              // already saved: content the new spelling still covers is
              // correctly reported as kept.
              affected={affectedSyncRows(
                syncState,
                [...persistedAllowlist, toServerEntry(newEntry, { newRowLists })],
                toServerEntry(oldEntry),
                { listsDefault }
              )}
              syncStateUnavailable={isNil(syncState)}
              replacing={replacing}
              onConfirm={onConfirm}
              onClose={onModalClose}
            />
          )}
          rowActions={supportsManageFiles ? manageFilesActions : undefined}
          testId={CONTENT_TABLE_TEST_ID}
        />
      ),
    },
    {
      isStatic: true,
      name: 'connection_details_heading',
      label: 'Connection details',
    },
    {
      type: 'text',
      name: 'label',
      label: 'Label',
      placeholder: 'Contoso HQ',
      labelTooltip:
        'Optional name for this tenant, shown in the connections list. Leave it blank and the ' +
        'list falls back to the SharePoint host.',
      customValidators: [Yup.string().max(100, 'Label must be 100 characters or fewer')],
    },
    {
      type: 'text',
      name: 'tenant_id',
      label: 'Tenant ID',
      required: true,
      placeholder: '72f988bf-86f1-41af-91ab-2d7cd011db47',
      labelTooltip: 'Your Entra tenant GUID.',
      disabled: isEdit,
      customValidators: [
        Yup.string()
          .required('Tenant ID is required')
          .matches(GUID_RE, 'Tenant ID must be a valid Entra tenant GUID'),
      ],
    },
    {
      type: 'text',
      name: 'tenant_hostname',
      label: 'SharePoint host',
      required: true,
      placeholder: 'contoso.sharepoint.com',
      labelTooltip:
        'From the site URL, e.g. contoso.sharepoint.com. A pasted https:// URL is fine.',
      customValidators: [
        Yup.string()
          .required('SharePoint host is required')
          .matches(HOST_RE, 'Host must be a *.sharepoint.com host')
          .test(
            'not-the-onedrive-host',
            "That is the OneDrive host, where every site is an employee's personal site. Use the organization host, e.g. contoso.sharepoint.com",
            (value) => !PERSONAL_HOST_RE.test((value || '').trim())
          ),
      ],
    },
  ]

  const existingValues = isEdit
    ? {
        // The server returns null for an unnamed connection; Formik needs '' or
        // the input renders uncontrolled.
        label: connection.label ?? '',
        tenant_id: connection.tenant_id,
        tenant_hostname: connection.tenant_hostname,
        allowlist: toFormAllowlist(connection.allowlist),
      }
    : // A preserved draft's typed values; undefined starts the form blank.
      draftValues

  return (
    <>
      <div className="field-help small-text mbs" data-testid="sharepoint-setup-hint">
        Before syncing, your Microsoft 365 admin must approve the Balto SharePoint Integration app
        for your tenant and grant it read access to each site below. Contact Balto support for the
        setup guide.
      </div>
      {isEdit && <ConnectionStatusRow connection={connection} />}
      {!isNil(approvalTarget) && (
        <ConfirmModal
          show
          onClose={() => {
            if (!approving) {
              setApprovalTarget(null)
            }
          }}
          title="Approve this library’s size?"
          confirmLabel="Approve"
          busyLabel="Approving…"
          busy={approving}
          onConfirm={approveSize}
          testIdPrefix="sharepoint-size-approval"
        >
          <p>
            {libraryLabelOf(approvalTarget)} has {approvalTarget.included_files.toLocaleString()}{' '}
            files to index. Approving lets the next sync index all of them. A library this size can
            take weeks to finish and uses a large share of the search index every customer shares.
          </p>
        </ConfirmModal>
      )}
      {confirmingListsOff && (
        <ConfirmModal
          show
          onClose={() => setConfirmingListsOff(false)}
          title="Turn off lists and purge their documents?"
          confirmLabel="Turn off and purge"
          busyLabel="Purging…"
          busy={togglingLists}
          destructive
          onConfirm={handleConfirmListsOff}
          testIdPrefix="sharepoint-lists-off"
        >
          <p>
            This stops syncing every list discovered on the sites of{' '}
            <strong>{connection.tenant_hostname}</strong> and immediately deletes the documents
            built from them. Lists named on their own row stay.
          </p>
          <p>Turning lists back on re-ingests them on the next sync.</p>
        </ConfirmModal>
      )}
      <AbstractForm
        schema={schema}
        onSubmit={handleSave}
        onClose={onClose}
        // A half-finished inline edit counts as unsaved input for the modal's
        // close guard, alongside Formik's own dirty state.
        onDirty={(dirty) => onDirty?.(dirty || editingEntry)}
        onFormikReady={(formik) => {
          formikRef.current = formik
          onFormikReady?.(formik)
        }}
        isModal
        buttonLabel="Save"
        buttonProps={saveButtonProps}
        closeButtonLabel={closeLabel}
        footerStart={
          isEdit ? (
            <>
              <Button
                type="button"
                critical={connection.active}
                brand={!connection.active}
                onClick={handleToggleActive}
              >
                {connection.active ? 'Pause syncing' : 'Resume syncing'}
              </Button>
              <Button
                type="button"
                loading={togglingLists}
                {...toggleListsProps}
                onClick={handleToggleLists}
              >
                {listsEnabled ? 'Turn off lists' : 'Turn on lists'}
              </Button>
              {footerActions}
            </>
          ) : null
        }
        existingValues={existingValues}
      />
      {/* Mounted only while open: a save that outlives the modal reports by toast, not onClose. */}
      {!isNil(manageFilesTarget) && (
        <ManageFilesModal
          connectionId={connection.id}
          subtitle={manageFilesTarget.subtitle}
          roots={manageFilesTarget.roots}
          onClose={() => setManageFilesTarget(null)}
        />
      )}
    </>
  )
}
